import { useReactiveVar } from '@apollo/client'
import { useTranslations } from '@npco/utils-translations'
import { showErrorToast } from '@npco/zeller-design-system'
import { ApiCallType, useRedirectToMFA } from 'features/MFA'

import { rvOptimisticDebitCardTransactions } from 'graphql/reactiveVariables'
import {
  FALLBACK_TO_DE,
  useSendContactTransfer as useSendContactTransferMutation,
} from 'hooks/banking'
import { AuthScope } from 'types/auth'
import {
  DAILY_TRANSFER_LIMIT_EXCEEDED,
  MFA_ERROR,
  UNEXPECTED_ERROR,
} from 'types/errors'
import { useContactTransferValues } from 'pages/Transfer/hooks/useSendContactTransfer/useContactTransferValues'
import { createContactOptimisticTransaction } from 'pages/Transfer/optimisticTransactions.utils'
import { useTransferState } from 'pages/Transfer/Transfer.context'
import {
  TransferContactState,
  TransferEvent,
  TransferGeneralState,
} from 'pages/Transfer/Transfer.stateMachine'

import { contactTransferTranslations } from '../../Transfer.i18n'

export const CURRENCY = 'AUD'

export const useSendContactTransfer = () => {
  const t = useTranslations(contactTransferTranslations)
  const { formValues, fromAccount, toAccount, selectedContact } =
    useContactTransferValues()
  const optimisticTransactions = useReactiveVar(
    rvOptimisticDebitCardTransactions
  )
  const { redirectToMFA } = useRedirectToMFA()

  const {
    transferState: [state, send],
  } = useTransferState()

  const { sendContactTransfer } = useSendContactTransferMutation()

  return async () => {
    const forceDeOut = state.matches(
      `${TransferGeneralState.ContactTransfer}.${TransferContactState.DeOutFallbackSummary}`
    )
    send(TransferEvent.Continue)

    const result = await sendContactTransfer({
      payerAccountUuid: fromAccount.id,
      payeeAccountUuid: formValues.to ?? '',
      payerReference: formValues.reference,
      payeeReference: formValues.recipientReference,
      amountInCents: Math.round(Number(formValues.amount) * 100),
      forceDeOut,
    })

    if (result === DAILY_TRANSFER_LIMIT_EXCEEDED) {
      showErrorToast(t('dailyMaxLimitTransferError'))
      send(TransferEvent.Back)
      return
    }

    if (result === UNEXPECTED_ERROR) {
      send(TransferEvent.Error)
      return
    }

    if (result === MFA_ERROR) {
      redirectToMFA(
        {
          apiCallType: ApiCallType.Transfer,
          variables: {
            payerAccountUuid: fromAccount.id,
            payeeAccountUuid: formValues.to ?? '',
            payerReference: formValues.reference,
            payeeReference: formValues.recipientReference,
            amountInCents: Math.round(Number(formValues.amount) * 100),
            forceDeOut,
          },
          scope: AuthScope.SENSITIVE,
          contact: selectedContact,
        },
        () => {
          send(TransferEvent.Back)
        }
      )
      return
    }

    if (result === FALLBACK_TO_DE) {
      send(TransferEvent.DeFallbackRetry)
      return
    }

    // Update rvOptimisticDebitCardTransactions with transaction
    const now = new Date().toISOString()

    const updatedOptimisticTransactions = [
      createContactOptimisticTransaction({
        id: result.id,
        amountInCents: Math.round(Number(formValues.amount) * 100),
        reference: formValues.reference,
        referencePayee: formValues.recipientReference,
        selectedContact,
        fromAccount,
        toAccount,
        timestamp: now,
        type: result.transferType,
      }),
      ...optimisticTransactions,
    ]

    rvOptimisticDebitCardTransactions(updatedOptimisticTransactions)

    send(TransferEvent.Continue)
  }
}
